// Refund Policy for CareerOS-AI.
import { Link } from "react-router-dom";
import LegalLayout, { bullets } from "./LegalLayout";

const EFFECTIVE = "July 16, 2026";

const SECTIONS = [
  { n: 1, id: "principles", title: "Our position",
    body: (<>
      <p>CareerOS-AI is a subscription service. In general, subscription fees are non-refundable once the billing period has started, because we begin providing the Service (including AI compute time) as soon as you subscribe.</p>
      <p>That said, we handle refund requests fairly. Where required by law we exceed the minimum. The rest of this policy explains when refunds are available and how to request one.</p>
    </>) },
  { n: 2, id: "eligibility", title: "When refunds are available",
    body: (<>
      <p>You may be eligible for a refund in any of these cases:</p>
      {bullets([
        "Billing error — you were charged the wrong amount, charged twice, or charged after cancellation.",
        "Unauthorized charge — a family member or unauthorized party subscribed on your card. Contact us before initiating a chargeback.",
        "Extended service unavailability — an outage of our platform that materially prevents you from using the paid features for an extended period.",
        "Statutory right — where your local consumer-protection law entitles you to a refund (e.g. EU/UK 14-day cooling-off for digital services subject to conditions, provincial consumer-protection statutes in Canada).",
      ])}
    </>) },
  { n: 3, id: "not-eligible", title: "When refunds are not available",
    body: (<>
      <p>The following are generally not refundable:</p>
      {bullets([
        "Change of mind after the billing period has started (except where statutory rights apply).",
        "Partial months. If you cancel mid-cycle you keep access until the end of the paid period; we do not prorate a refund.",
        "Failure to cancel before an auto-renewal. Please cancel from Settings before your renewal date.",
        "Dissatisfaction with AI output quality when the feature is functioning as described.",
        "Loss of access due to a violation of our Terms or Acceptable Use Policy.",
      ])}
    </>) },
  { n: 4, id: "how-to-request", title: "How to request a refund",
    body: (<>
      <p>Email <span className="font-mono">support@careeros-ai.com</span> with:</p>
      {bullets([
        "The email address on your CareerOS account.",
        "The date and approximate amount of the charge.",
        "The reason you are requesting a refund.",
        "If billing error — a screenshot of the charge from your card statement.",
      ])}
      <p>We typically respond within 2 business days. Approved refunds are processed to the original payment method within 5–10 business days depending on your bank.</p>
    </>) },
  { n: 5, id: "annual-plans", title: "Annual plans",
    body: (<>
      <p>Annual plans are a discounted commitment. If you cancel an annual plan mid-term:</p>
      {bullets([
        "You retain access until the end of the paid term.",
        "You are not entitled to a refund of the unused portion, except in the eligibility cases listed in section 2.",
      ])}
    </>) },
  { n: 6, id: "credits", title: "Account credit",
    body: (<>
      <p>In some cases we may offer account credit (applied to your next renewal) instead of a cash refund. Credit is optional — you can decline it and we will process the refund per section 4.</p>
    </>) },
  { n: 7, id: "chargebacks", title: "Chargebacks",
    body: <p>Please contact us before initiating a chargeback with your bank. Unresolved chargebacks may result in immediate account suspension per our <Link to="/subscription-policy">Subscription &amp; Billing Policy</Link>.</p> },
  { n: 8, id: "changes", title: "Changes to this policy",
    body: <p>We may update this policy. Material changes are communicated on the website or by email. The &quot;Last Updated&quot; date reflects the current version.</p> },
  { n: 9, id: "contact", title: "Contact",
    body: <p>Refund requests and questions: <span className="font-mono">support@careeros-ai.com</span>.</p> },
];

const INTRO = (
  <p>This Refund Policy explains when subscription fees can be refunded, when they cannot, and how to request a refund. It is part of our <Link to="/terms">Terms</Link> and complements our <Link to="/subscription-policy">Subscription &amp; Billing Policy</Link>.</p>
);

export default function RefundPolicy() {
  return (
    <LegalLayout
      eyebrow="Legal · Refunds"
      title="Refund Policy"
      subtitle="for CareerOS-AI"
      effectiveDate={EFFECTIVE}
      lastUpdated={EFFECTIVE}
      intro={INTRO}
      sections={SECTIONS}
      currentPath="/refund-policy"
    />
  );
}
